import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ChatMessageDTO } from '../MessageType';
import { formatMessageTime } from '../MessageUtil';
import { renderMessageCard } from '../cards/MessageCardRegistry';
import { messageModel } from '../MessageModel';
import { getSelfUserBrief, OFFICIAL_ACCOUNT_ID } from '../MessageConfig';
import { globalStore } from '../../GlobalStore';
import { CachedImage } from '../../components/CachedImage';
import { MessageTextContent } from './MessageTextContent';

interface ChatMessageItemProps {
  message: ChatMessageDTO;
  isSelf: boolean;
}

export const ChatMessageItem: React.FC<ChatMessageItemProps> = observer(
  ({ message, isSelf }) => {
    const [senderName, setSenderName] = useState('');
    const [avatar, setAvatar] = useState('');
    const buyerAvatar = globalStore.currentBuyer?.avatarUrl;
    const buyerNickname = globalStore.currentBuyer?.nickname;

    useEffect(() => {
      if (isSelf) {
        const self = getSelfUserBrief();
        setSenderName(self.name);
        setAvatar(self.avatar);
        return;
      }
      messageModel.getUserInfo(message.fromUserId, message.roomId).then((info) => {
        setSenderName(info.name);
        setAvatar(info.avatar);
      });
    }, [message.fromUserId, isSelf, buyerAvatar, buyerNickname]);

    if (message.isSystem || message.isWithdraw) {
      return (
        <div className="message-system-row">
          {message.isWithdraw ? '消息已撤回' : message.content}
        </div>
      );
    }

    const isOfficial = message.fromUserId === OFFICIAL_ACCOUNT_ID;
    const displayName = isSelf
      ? senderName || '我'
      : isOfficial
        ? '圆领平台官方'
        : senderName;

    let body: React.ReactNode;
    if (message.isCard) {
      try {
        const card = JSON.parse(message.content);
        body = renderMessageCard({ content: card, isSelf });
      } catch {
        body = <span>卡片消息解析失败</span>;
      }
    } else {
      body = (
        <span className="message-text">
          <MessageTextContent text={message.content} />
        </span>
      );
    }

    return (
      <div className={`message-row${isSelf ? ' self' : ''}`}>
        <div className="message-avatar">
          {avatar ? (
            <CachedImage
              url={avatar}
              style={{ width: 36, height: 36, borderRadius: 4 }}
            />
          ) : (
            <div className="message-room-avatar-placeholder small" />
          )}
        </div>
        <div className="message-bubble-wrap">
          <div className={`message-meta${isSelf ? ' self' : ''}`}>
            {isSelf ? (
              <>
                <span className="message-time">
                  {formatMessageTime(message.time)}
                </span>
                <span className="message-sender-name">{displayName}</span>
              </>
            ) : (
              <>
                <span className="message-sender-name">{displayName}</span>
                <span className="message-time">
                  {formatMessageTime(message.time)}
                </span>
              </>
            )}
          </div>
          <div
            className={`message-bubble${isSelf ? ' self' : ''}${message.isCard ? ' card' : ''}`}
          >
            {body}
          </div>
        </div>
      </div>
    );
  },
);
